import { Button } from "../../elements/button/button";
import type {
  AspectRatioFourThree,
  AspectRatioSixteenNine,
  AspectRatioSquare,
} from "../../elements/image/image";
import { Image } from "../../elements/image/image";
import { Text } from "../../elements/text/text";
import { cn } from "../../lib/utils";

export interface CardProductProps {
  id: string;
  variant: "simple" | "detailed";
  href?: string;
  product: {
    name: string;
    code?: string;
    image: {
      src: string;
      alt: string;
      aspectRatio:
        | AspectRatioSquare
        | AspectRatioFourThree
        | AspectRatioSixteenNine;
    };
    price?: string;
    dealerPriceLabel?: string;
    dealerPriceValue?: string;
    rrpLabel?: string;
    rrpValue?: string;
  };
  microcopies: {
    productCode: string;
  };
}

export function CardProduct(props: CardProductProps) {
  const gapClass = props.variant === "simple" ? "gap-4" : "gap-2";
  const cardClassName = cn(
    "flex h-full w-full flex-col items-start rounded-lg border border-stone-200 bg-white p-5 text-inherit no-underline",
    gapClass,
    props.href && "cursor-pointer"
  );

  const content = (
    <>
      {/* Product Image */}
      <div className="w-full shrink-0 overflow-hidden rounded-[6px]">
        <Image
          alt={props.product.image.alt}
          aspectRatio={props.product.image.aspectRatio}
          fill
          src={props.product.image.src}
        />
      </div>

      {props.variant === "simple" ? (
        <>
          {/* Product Info Container - Simple Variant */}
          <div className="flex w-full flex-col items-center justify-center gap-2">
            {/* Product Name */}
            <div className="flex min-h-16 w-full items-center justify-center text-center">
              <div className="line-clamp-2 overflow-hidden">
                <Text variant="h3">{props.product.name}</Text>
              </div>
            </div>

            {/* Product Price */}
            {props.product.price && (
              <div className="flex items-center py-1">
                <Text variant="muted">{props.product.price}</Text>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Detailed Variant */}
          {/* Product Name */}
          <div className="flex min-h-16 w-full items-center justify-center self-stretch text-center">
            <div className="line-clamp-2 overflow-hidden">
              <Text variant="h3">{props.product.name}</Text>
            </div>
          </div>

          {/* Product Code */}
          {props.product.code && (
            <div className="w-full self-stretch text-center">
              <div className="flex items-center justify-center gap-2.5">
                <Text variant="body-sm">{props.microcopies.productCode}</Text>
                <Text variant="body-sm">{props.product.code}</Text>
              </div>
            </div>
          )}

          {/* Price Container */}
          <div className="flex w-full flex-col items-center gap-1 self-stretch">
            {/* Dealer Price Container */}
            {props.product.dealerPriceLabel &&
              props.product.dealerPriceValue && (
                <div className="flex items-center justify-center gap-2.5">
                  <Text variant="body-sm">
                    {props.product.dealerPriceLabel}
                  </Text>
                  <Text variant="body-sm">
                    {props.product.dealerPriceValue}
                  </Text>
                </div>
              )}

            {/* RRP Container */}
            {props.product.rrpLabel && props.product.rrpValue && (
              <div className="flex items-center gap-2.5">
                <Text variant="muted">{props.product.rrpLabel}</Text>
                <Text variant="muted">{props.product.rrpValue}</Text>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );

  if (props.href) {
    return (
      <Button
        className="block h-full w-full shrink whitespace-normal bg-transparent p-0 text-inherit no-underline shadow-none hover:bg-transparent hover:no-underline focus-visible:ring-0"
        href={props.href}
        unstyled={true}
      >
        <div className={cardClassName} key={props.id}>
          {content}
        </div>
      </Button>
    );
  }

  return (
    <div className={cardClassName} key={props.id}>
      {content}
    </div>
  );
}
